/**
 * Pre-picking rooms for a party, pure (batch-29, item R1; plan §4.7). It runs in the browser on the types the server
 * reported as available for this party and these nights. The number of rooms the guest asked for is never changed
 * silently: when it does not work the result says how many rooms everyone needs.
 */
import type { BedSettings, RoomBeds } from './beds';
import { placeParty, type Counts, type Placement } from './placement';

export interface Candidate {
  code: string;
  beds: RoomBeds;
  /** how many rooms of this type this guest may take (the server's bounded answer; never printed) */
  upTo: number;
  /** the price for the stay, when rooms are charged */
  stayCents?: number | null;
  /** the hotel's own order of its types */
  order: number;
  /** how many of the type's beds are sofa beds (0 or missing = none); the pre-pick keeps adults off them when it can */
  sofaBeds?: number;
}
export interface PrePick {
  /** one code per room, in room order */
  rooms: string[];
  /** the rooms everyone needs here; equal to `asked` when the guest's own number works */
  needed: number;
  asked: number;
  placement: Placement;
}

/** The most rooms one booking holds unless the event sets its own (item R17). */
const MAX_ROOMS = 10;
/** A type that is not set takes any party, so it sorts as the largest. */
const size = (t: RoomBeds): number => (t.beds === null ? Number.MAX_SAFE_INTEGER : t.maxPeople);
/**
 * How many people of the oldest group a room of this type holds without one of them on a sofa bed: its other beds, and
 * a second person in each of those that is a bed for two when the event allows it. Null = no limit (no sofa bed, or not set).
 */
function adultsOffSofa(c: Candidate, s: BedSettings): number | null {
  const sofa = Math.max(0, Math.trunc(c.sofaBeds ?? 0));
  if (c.beds.beds === null || sofa === 0) return null;
  const real = Math.max(0, c.beds.beds - sofa);
  return real + (s.twoPerBed ? Math.min(c.beds.bedsForTwo, real) : 0);
}

export function prePick(candidates: readonly Candidate[], asked: number, party: Counts, s: BedSettings, groupOrder: readonly string[], cribKey: string | null, maxRooms: number = MAX_ROOMS): PrePick | null {
  const sorted = candidates.filter((c) => c.upTo > 0)
    .sort((a, b) => ((a.stayCents ?? 0) - (b.stayCents ?? 0)) || (a.order - b.order) || a.code.localeCompare(b.code));
  const byCode = new Map(sorted.map((c) => [c.code, c]));
  const people = Object.values(party).reduce((n, v) => n + Math.max(0, v), 0);
  const nonBabies = people - (cribKey ? Math.max(0, party[cribKey] ?? 0) : 0);
  const most = Math.min(Math.max(1, Math.trunc(maxRooms) || MAX_ROOMS), nonBabies);
  if (!sorted.length || nonBabies < 1) return null;
  // the oldest group (adults): sofa beds go to the younger guests first (owner, 2026-10-18: two adults are offered two
  // queens, one adult and one teen a king with a sofa bed). Only the pre-pick prefers it; the guest may still choose any room.
  const oldest = [...groupOrder].reverse().find((k) => k !== cribKey) ?? null;
  const attempt = (codes: string[], n: number, offSofa: boolean): PrePick | null => {
    const placement = placeParty(codes.map((code) => byCode.get(code)!.beds), s, party, groupOrder, cribKey);
    const empty = placement.rooms.some((r) => Object.values(r.people).every((v) => !v));
    if (placement.left !== 0 || empty) return null;
    if (offSofa && oldest) {
      for (let i = 0; i < codes.length; i++) {
        const cap = adultsOffSofa(byCode.get(codes[i]!)!, s);
        if (cap !== null && (placement.rooms[i]!.people[oldest] ?? 0) > cap) return null;
      }
    }
    return { rooms: codes, needed: n, asked, placement };
  };
  const largest = [...sorted].sort((a, b) => (size(b.beds) - size(a.beds)) || (sorted.indexOf(a) - sorted.indexOf(b)));
  const cost = (codes: readonly string[]): number => codes.reduce((sum, code) => sum + (byCode.get(code)!.stayCents ?? 0), 0);
  /** The first pick with n rooms: one type with enough rooms, cheapest first; then mixed types, the n slots filled largest first, within what each type has. */
  const search = (n: number, offSofa: boolean): PrePick | null => {
    for (const c of sorted) {
      if (c.upTo < n) continue;
      const got = attempt(Array.from({ length: n }, () => c.code), n, offSofa);
      if (got) return got;
    }
    const codes: string[] = [];
    for (const c of largest) for (let k = 0; k < c.upTo && codes.length < n; k++) codes.push(c.code);
    return codes.length === n ? attempt(codes, n, offSofa) : null;
  };
  for (let n = Math.max(1, Math.min(asked, most)); n <= most; n++) {
    // today's pick first; then, with the same number of rooms and for no more money, the one with every adult off a sofa bed
    const base = search(n, false);
    if (!base) continue;
    const steered = search(n, true);
    return steered && cost(steered.rooms) <= cost(base.rooms) ? steered : base;
  }
  return null;
}
